/** Ship bodies: what a grid is made of (ShipStats), ship coordinates, flight control, breaking apart and furnaces. */
import { ASSIST, CURRENTS, DRIFT, FURNACE, HARPOON, SHIP, SHIP_GRID, STREAM } from "../constants";
import { clamp, v2, vAdd, vDot, vLen, vRotate, vScale, vSub, type Box, type Vec2 } from "../math";
import { T, TILES, blocksShips, bowGunFacing, isBuilt, isHelm, isMachine, isMachineAnchor, isOpenHatch, isSolid, makeGrid, pieceAnchor, pieceDef, pieceOf, setShape, shapeAt, steamExhaust, tileAt, type Grid, type TileId } from "../tiles";
import { ITEMS, addItems, count, fuelToStoke, waterToStoke, type Inventory, type ItemId } from "../items";
import { type AsteroidDef, type ShipState, type TurretState, type WorldDef } from "./types";
import { shipWaterFraction, tetherPoint, waterAt } from "./character";
import { currentAt } from "./generate";
import { flowAt } from "./flow";

// ------------------------------------------------------------------ bodies

export interface ShipStats {
  /** Indices of every non-empty tile. */
  cells: number[];
  mass: number;
  /** Steam engines, all told. */
  engines: number;
  /** Steam engines by which way they push the ship, in its own axes (the exhaust points the other way). */
  push: { up: number; down: number; left: number; right: number };
  /** Furnaces: each holds FURNACE.capacity of fuel and of water. */
  furnaces: number;
  /** Helms. A ship with one holds station when nobody flies it; one without drifts. */
  helms: number;
  /** Its turrets: the grid index of each one's anchor (bottom-left seat tile). */
  turrets: number[];
  /**
   * 1 for every tile shut in by the hull (not reachable from outside the grid
   * through empty space): what outsiders don't get to see. Back walls, ladders,
   * the furnace, turret seats, and anyone standing among them.
   */
  interior: Uint8Array;
  /** Local bounds of the non-empty tiles, or null for an empty grid. */
  bounds: Box | null;
  /** Distance from the grid centre (the pivot) that encloses every tile's collision circle. */
  radius: number;
  /** Pumps aboard (see PUMP). */
  pumps: number;
  /** Bow guns (grid indices), fired by the pilot. */
  bowGuns: number[];
  /** Winch anchors (grid indices). */
  winches: number[];
  /** Harpoon anchors (grid indices; see HARPOON). */
  harpoons: number[];
  /** The middle of its blocks (grid coordinates), every block weighing the same. */
  centerOfMass: Vec2 | null;
}

/** What the pilot is asking for: -1..1 each. Persists between inputs while held. */
export interface ShipControl {
  /** -1..1: +1 fires the engines that push the ship toward its own +X (the stern ones; D), -1 the bow ones (A). */
  x: number;
  /** -1..1: +1 fires the engines that push toward its own +Y (the ones underneath; W), -1 those on top (S). */
  y: number;
  /** -1..1: +1 turns counter-clockwise (Q), -1 clockwise (E). */
  turn: number;
}

export interface ShipBody {
  state: ShipState;
  grid: Grid;
  stats: ShipStats;
  control?: ShipControl;
  /**
   * Simulated by another area (its key): a read-only copy near the border, so
   * this area's ships can bump into it and cannonballs can hit it. Moved by
   * that area's updates, never by this one.
   */
  ghost?: string;
  /** Moored to a drifting rock that moved it last step (see carryShips): how fast it was carried, blocks/s. */
  carried?: Vec2;
}

export interface AsteroidBody {
  def: AsteroidDef;
  grid: Grid;
  /** Turrets built on it, by anchor index (see TurretHost). */
  turrets?: Record<string, TurretState>;
}

/** Everything the deterministic step functions need to read. */
export interface Sim {
  world: WorldDef;
  asteroids: AsteroidBody[];
  ships: Map<string, ShipBody>;
  /** Vines that are cut through (see VINES), by id: nobody can climb them till they grow back. */
  vinesCut?: Set<string>;
  /** Drifting rocks knocked off their paths (see rockMotion.ts), by id. */
  knocks?: Map<string, Knock>;
}

/** How far a drifting rock is knocked off its path, and how fast it's going relative to it (blocks, blocks/s). */
export interface Knock {
  x: number;
  y: number;
  vx: number;
  vy: number;
  /** A stream rock: how far across the band it has settled from where it was made (see stepKnocks), blocks to the left of the belt's way. */
  o?: number;
}

export function shipStats(grid: Grid): ShipStats {
  const cells: number[] = [];
  const turrets: number[] = [];
  let engines = 0;
  let furnaces = 0;
  const push = { up: 0, down: 0, left: 0, right: 0 };
  let helms = 0;
  let bounds: Box | null = null;
  let radius = 0;
  let pumps = 0;
  const bowGuns: number[] = [];
  const winches: number[] = [];
  const harpoons: number[] = [];
  let sx = 0;
  let sy = 0;
  for (let i = 0; i < grid.tiles.length; i++) {
    const t = grid.tiles[i]!;
    if (t === T.EMPTY) continue;
    const x = i % grid.w;
    const y = Math.floor(i / grid.w);
    cells.push(i);
    sx += x + 0.5;
    sy += y + 0.5;
    if (isMachine(t, "pump")) pumps++;
    else if (bowGunFacing(t)) bowGuns.push(i);
    // Pieces count once, at their anchor.
    const part = pieceOf(t);
    const anchor = part && pieceAnchor(grid, i) === i ? TILES[t]!.block.part : undefined;
    if (anchor === "furnace") furnaces++;
    if (anchor === "winch") winches.push(i);
    if (anchor === "harpoon") harpoons.push(i);
    const exhaust = steamExhaust(t);
    if (exhaust) {
      engines++;
      if (exhaust[1] < 0) push.up++;
      else if (exhaust[1] > 0) push.down++;
      else if (exhaust[0] < 0) push.right++;
      else push.left++;
    }
    if (anchor === "helm") helms++;
    if (anchor === "turret") turrets.push(i);
    radius = Math.max(radius, Math.hypot(x + 0.5 - grid.w / 2, y + 0.5 - grid.h / 2) + SHIP.cellRadius);
    if (!bounds) bounds = { x0: x, y0: y, x1: x + 1, y1: y + 1 };
    else {
      bounds.x0 = Math.min(bounds.x0, x);
      bounds.y0 = Math.min(bounds.y0, y);
      bounds.x1 = Math.max(bounds.x1, x + 1);
      bounds.y1 = Math.max(bounds.y1, y + 1);
    }
  }
  const n = cells.length;
  return {
    cells,
    mass: n,
    engines,
    push,
    furnaces,
    helms,
    turrets,
    interior: shutIn(grid),
    bounds,
    radius,
    pumps,
    bowGuns,
    winches,
    harpoons,
    centerOfMass: n ? v2(sx / n, sy / n) : null,
  };
}

/**
 * How a ship would fly, for the build readout: acceleration each way (m/s^2)
 * from its engines, and how far its middle of mass sits from its pivot.
 */
export function flightReadout(ship: ShipBody): { lift: number; sink: number; forward: number; back: number; turn: number; offset: Vec2 } {
  const inertia = shipInertia(ship);
  const k = SHIP.enginePower / inertia;
  const { up, down, left, right } = ship.stats.push;
  const com = ship.stats.centerOfMass ?? v2(ship.grid.w / 2, ship.grid.h / 2);
  return {
    lift: up * k,
    sink: down * k,
    forward: right * k,
    back: left * k,
    turn: (ship.stats.engines * SHIP.turnPower) / inertia,
    offset: v2(com.x - ship.grid.w / 2, com.y - ship.grid.h / 2),
  };
}

/** What a ship weighs for flying: its blocks. */
export function shipInertia(ship: ShipBody): number {
  return SHIP.baseMass + ship.stats.mass;
}

/**
 * Tiles shut in by a ship's hull: not solid, and not reachable from the
 * grid's edge through empty tiles. An open hatch lets the outside in: through
 * it, everything open is reachable (and seen).
 */
export function shutIn(grid: Grid): Uint8Array {
  const { w, h, tiles } = grid;
  const outside = new Uint8Array(w * h);
  const flood = (queue: number[], passes: (t: TileId) => boolean) => {
    const visit = (i: number) => {
      if (outside[i] || !passes(tiles[i]!)) return;
      outside[i] = 1;
      queue.push(i);
    };
    for (const i of queue) outside[i] = 1;
    for (let k = 0; k < queue.length; k++) {
      const i = queue[k]!;
      const x = i % w;
      if (x > 0) visit(i - 1);
      if (x < w - 1) visit(i + 1);
      if (i >= w) visit(i - w);
      if (i < (h - 1) * w) visit(i + w);
    }
    return queue;
  };
  const edge: number[] = [];
  for (let x = 0; x < w; x++) edge.push(x, (h - 1) * w + x);
  for (let y = 0; y < h; y++) edge.push(y * w, y * w + w - 1);
  const openHatch = isOpenHatch;
  // Lattice is see-through: it doesn't seal a ship.
  const reached = flood(
    edge.filter((i) => tiles[i] === T.EMPTY),
    (t) => t === T.EMPTY || openHatch(t) || isBuilt(t, "lattice"),
  );
  flood(
    reached.filter((i) => openHatch(tiles[i]!)),
    (t) => !isSolid(t),
  );
  const inside = new Uint8Array(w * h);
  for (let i = 0; i < tiles.length; i++) if (!outside[i] && !isSolid(tiles[i]!)) inside[i] = 1;
  return inside;
}

export function asteroidBox(def: AsteroidDef): Box {
  return { x0: def.ox, y0: def.oy, x1: def.ox + def.w, y1: def.oy + def.h };
}

// ------------------------------------------------------- ship coordinates

/**
 * Ship-local grid coordinates to world, for a ship at `position` turned by
 * `angle` about the centre of its grid.
 */
export function poseToWorld(position: Vec2, angle: number, grid: Grid, local: Vec2): Vec2 {
  const hx = grid.w / 2;
  const hy = grid.h / 2;
  const r = vRotate(v2(local.x - hx, local.y - hy), angle);
  return v2(position.x + hx + r.x, position.y + hy + r.y);
}

export function shipToWorld(ship: ShipBody, local: Vec2): Vec2 {
  return poseToWorld(ship.state.position, ship.state.angle, ship.grid, local);
}

export function worldToShip(ship: ShipBody, world: Vec2): Vec2 {
  const hx = ship.grid.w / 2;
  const hy = ship.grid.h / 2;
  const p = ship.state.position;
  const r = vRotate(v2(world.x - p.x - hx, world.y - p.y - hy), -ship.state.angle);
  return v2(r.x + hx, r.y + hy);
}

export function shipCenter(ship: ShipBody): Vec2 {
  return v2(ship.state.position.x + ship.grid.w / 2, ship.state.position.y + ship.grid.h / 2);
}

/**
 * Flight control for one step. The pilot's W/A/S/D fire the steam engines
 * that push that way (W the ones underneath, S those on top, D the stern ones,
 * A the bow ones), each pushing in the ship's own axes and burning a unit of
 * fuel and of water per second; Q/E turn the ship, working every engine a
 * little. With fuel or water run dry, nothing fires. Then drag
 * and speed caps; ShipPhysics moves the ship and resolves collisions. A ship
 * with no pilot coasts to a stop if it has a helm, and otherwise drifts on,
 * barely slowing. `water` (0..1, from shipWaterFraction) adds drag while
 * submerged.
 */
export function applyShipControl(ship: ShipBody, dt: number, water = 0, env: ShipEnv = {}): void {
  const s = ship.state;
  const cx = clamp(ship.control?.x ?? 0, -1, 1);
  const cy = clamp(ship.control?.y ?? 0, -1, 1);
  let turn = clamp(ship.control?.turn ?? 0, -1, 1);
  for (const t of Object.values(s.turrets)) t.cooldown = Math.max(0, t.cooldown - dt);
  if (s.gunCooldown) s.gunCooldown = Math.max(0, s.gunCooldown - dt);
  const drifts = ship.stats.helms === 0;
  const drag = drifts ? DRIFT.drag : SHIP.drag;
  const angularDrag = drifts ? DRIFT.angularDrag : SHIP.angularDrag;
  const inertia = shipInertia(ship);

  // Engines firing each way, as many as there are, while there's steam.
  const steam = s.fuel > 0 && s.water > 0;
  const { up, down, left, right } = ship.stats.push;
  const fx = steam ? cx * (cx > 0 ? right : left) : 0;
  const fy = steam ? cy * (cy > 0 ? up : down) : 0;
  // Rotation assist: with Q/E let go (and someone at the helm), work against the spin, so the ship holds its heading.
  const turnAccel = (ship.stats.engines * SHIP.turnPower) / inertia;
  if (turn === 0 && ship.control && Math.abs(s.angularVelocity) > ASSIST.deadband && turnAccel > 0) {
    turn = clamp((-s.angularVelocity / (turnAccel * dt)) * 0.5, -ASSIST.strength, ASSIST.strength);
  }
  // Turning (Q/E) works the whole engine set a little, so it needs steam too.
  const turning = steam && ship.stats.engines ? turn : 0;
  const firing = Math.abs(fx) + Math.abs(fy) + Math.abs(turning) * ship.stats.engines * SHIP.turnBurn;
  s.fuel = Math.max(0, s.fuel - firing * dt);
  s.water = Math.max(0, s.water - firing * dt);
  s.throttle = { x: fx === 0 ? 0 : cx, y: fy === 0 ? 0 : cy };

  s.angularVelocity += turning * turnAccel * dt;
  // Water drags harder on the side that's in it, which turns the ship.
  s.angularVelocity += (env.waterSpin ?? 0) * SHIP.waterDrag * dt;
  s.angularVelocity *= Math.max(0, 1 - (angularDrag + SHIP.waterDrag * water) * dt);
  s.angularVelocity = clamp(s.angularVelocity, -SHIP.maxTurnRate, SHIP.maxTurnRate);

  // An eel's arc on the hull stalls the engines near it: they fire, but push less (see INCOMERS.eel).
  const push = vScale(vRotate(v2(fx, fy), s.angle), (SHIP.enginePower / inertia) * (1 - clamp(s.stalled ?? 0, 0, 1)) * dt);
  s.velocity = vAdd(s.velocity, push);
  // The air slows it, against the flow where there is one (see flowAt): among the stream's rocks or a ring's it comes to rest going with them. A helmless one is brought round to it quicker.
  const flow = env.flow ?? v2();
  const flowing = flow.x !== 0 || flow.y !== 0;
  const slow = Math.max(0, 1 - (drag + SHIP.waterDrag * water + (flowing && drifts ? STREAM.grip : 0)) * dt);
  s.velocity = vAdd(flow, vScale(vSub(s.velocity, flow), slow));
  // A drift current carries it along (a helmless ship gives in to it more).
  if (env.current && (env.current.x || env.current.y)) {
    const grip = CURRENTS.grip * (drifts ? 2 : 1) * dt;
    s.velocity = vAdd(s.velocity, vScale(env.current, grip));
  }
  const speed = vLen(s.velocity);
  if (speed > SHIP.maxSpeed) s.velocity = vScale(s.velocity, SHIP.maxSpeed / speed);
  // Stuck in a rock or ship that isn't here (or not told where it is): the line is slack.
  pullTether(ship, env.tether === undefined ? (s.tether?.rock || s.tether?.ship ? undefined : s.tether?.point) : (env.tether ?? undefined));
}

/** Where a harpoon's line leaves the ship: where its gun pivots (see turretPivot), in the world. */
export function harpoonMount(ship: ShipBody, anchor: number): Vec2 {
  // Every family's harpoon is the same size.
  const { w, h } = pieceDef(T.IRONCLAD_HARPOON);
  return shipToWorld(ship, v2((anchor % ship.grid.w) + w / 2, Math.floor(anchor / ship.grid.w) + h * 0.8));
}

/** Is there still a harpoon at `anchor` (not dug out or shot off)? */
export const harpoonAt = (ship: ShipBody, anchor: number): boolean => pieceAnchor(ship.grid, anchor) === anchor && isMachineAnchor(ship.grid.tiles[anchor]!, "harpoon");

/**
 * A ship on a harpoon's line (see HARPOON) that has gone further than the
 * line allows is pulled back: the line stops it moving away and draws it
 * back in by how far it's stretched, tugging at the harpoon, so the ship
 * swings round to trail from it (or is dragged after a whale). Its harpoon
 * gone (dug out, shot off), it's free.
 */
function pullTether(ship: ShipBody, point: Vec2 | undefined): void {
  const s = ship.state;
  const t = s.tether;
  if (!t || !point) return;
  if (!harpoonAt(ship, t.anchor)) {
    s.tether = null;
    return;
  }
  const at = harpoonMount(ship, t.anchor);
  const off = vSub(at, point);
  const d = vLen(off);
  if (d <= t.length || d === 0) return;
  const n = vScale(off, 1 / d);
  const r = vSub(at, shipCenter(ship));
  // How fast the grapple is moving along the line (out is positive), and how fast it should be: back in.
  const out = vDot(vAdd(s.velocity, v2(-s.angularVelocity * r.y, s.angularVelocity * r.x)), n);
  const want = -(d - t.length) * HARPOON.stiffness;
  if (out <= want) return;
  // An impulse along the line at the grapple, shared between moving the ship and turning it.
  const arm = r.x * n.y - r.y * n.x;
  const gyration = Math.max(4, (ship.stats.radius * 0.6) ** 2);
  const j = (want - out) / (1 + (arm * arm) / gyration);
  s.velocity = vAdd(s.velocity, vScale(n, j));
  s.angularVelocity = clamp(s.angularVelocity + (arm * j) / gyration, -SHIP.maxTurnRate, SHIP.maxTurnRate);
}

/**
 * Ship `target`, hooked at world point `at` on a harpoon's line from
 * `from` as long as `length`, is towed: once the line's stretched, it's
 * stopped going further and drawn back in, as the harpooning ship is (half
 * each: they pull on each other).
 */
export function towShip(target: ShipBody, from: Vec2, at: Vec2, length: number): void {
  const off = vSub(at, from);
  const d = vLen(off);
  if (d <= length || d === 0) return;
  const n = vScale(off, 1 / d);
  const out = vDot(target.state.velocity, n);
  const want = -(d - length) * HARPOON.stiffness;
  if (out > want) target.state.velocity = vAdd(target.state.velocity, vScale(n, (want - out) * 0.5));
}

/**
 * Everything about where a ship is that flight control needs: how much of it
 * is under water, the drift current, and the turn from water on one side.
 * Server and client both call this, so a predicted ship flies the same.
 */
export function shipSurroundings(sim: Sim, ship: ShipBody): { water: number; env: ShipEnv } {
  const water = shipWaterFraction(sim, ship);
  const t = ship.state.tether;
  return {
    water,
    // A ship a rock is carrying (see carryShips) goes with the rock, not the flow too.
    env: { current: currentAt(sim.world, shipCenter(ship)), flow: ship.carried ? undefined : flowAt(sim.world, shipCenter(ship)), waterSpin: water > 0 && water < 1 ? shipWaterSpin(sim, ship) : 0, tether: t ? tetherPoint(t, sim) : undefined },
  };
}

/** What a ship's surroundings do to it this step (see applyShipControl). */
export interface ShipEnv {
  /** The drift current where it is (see currentAt). */
  current?: Vec2;
  /** The flow where it is (see flowAt): the stream's or a ring's, which the air goes with. */
  flow?: Vec2;
  /** Spin from water dragging on one side more than the other (see shipWaterSpin). */
  waterSpin?: number;
  /** Where its tether is hooked now (see tetherPoint): null if what it's stuck in isn't here (the line goes slack); missing, its point as it is. */
  tether?: Vec2 | null;
}

/**
 * Spin from water: every tile under water drags against how it's moving
 * (the ship's velocity plus its spin), so a ship half in water is pulled
 * round toward the wet side. Returns angular acceleration per unit of drag.
 */
export function shipWaterSpin(sim: Sim, ship: ShipBody): number {
  if (!sim.asteroids.some((a) => a.def.kind === "water")) return 0;
  const s = ship.state;
  const c = shipCenter(ship);
  let torque = 0;
  let inertia = 0;
  let wet = 0;
  for (const i of ship.stats.cells) {
    const w = shipToWorld(ship, v2((i % ship.grid.w) + 0.5, Math.floor(i / ship.grid.w) + 0.5));
    const r = vSub(w, c);
    inertia += r.x * r.x + r.y * r.y;
    if (!waterAt(sim, w)) continue;
    wet++;
    const vx = s.velocity.x - s.angularVelocity * r.y;
    const vy = s.velocity.y + s.angularVelocity * r.x;
    torque += r.x * -vy - r.y * -vx;
  }
  // All dry or all wet: the even drag is already in applyShipControl's `water`.
  if (!wet || wet === ship.stats.cells.length || inertia < 1e-6) return 0;
  return torque / inertia;
}

/**
 * The pieces a ship's blocks form: groups joined side to side, as lists of
 * tile indices. The piece that stays the ship comes first (the one with the
 * helm, otherwise the biggest). Any others have
 * broken off.
 */
export function shipPieces(ship: ShipBody): number[][] {
  const { w, h, tiles } = ship.grid;
  const piece = new Int32Array(w * h).fill(-1);
  const pieces: number[][] = [];
  for (let start = 0; start < tiles.length; start++) {
    if (tiles[start] === T.EMPTY || piece[start] !== -1) continue;
    const cells = [start];
    piece[start] = pieces.length;
    for (let k = 0; k < cells.length; k++) {
      const i = cells[k]!;
      const x = i % w;
      const y = (i - x) / w;
      for (const [nx, ny] of [[x + 1, y], [x - 1, y], [x, y + 1], [x, y - 1]] as const) {
        if (nx < 0 || ny < 0 || nx >= w || ny >= h) continue;
        const j = ny * w + nx;
        if (tiles[j] !== T.EMPTY && piece[j] === -1) {
          piece[j] = pieces.length;
          cells.push(j);
        }
      }
    }
    pieces.push(cells);
  }
  let keep = pieces.findIndex((p) => p.some((i) => isHelm(tiles[i]!)));
  if (keep < 0) pieces.forEach((p, i) => (keep = keep < 0 || p.length > pieces[keep]!.length ? i : keep));
  if (keep > 0) [pieces[0], pieces[keep]] = [pieces[keep]!, pieces[0]!];
  return pieces;
}

/**
 * A new ship holding `cells` of `from`'s grid (a piece that broke off), exactly where they
 * were and moving as they were, plus `kick` (m/s) so it drifts clear.
 */
export function breakOff(from: ShipBody, cells: number[], id: string, kick: Vec2): ShipBody {
  const grid = makeGrid(from.grid.w, from.grid.h);
  for (const i of cells) {
    grid.tiles[i] = from.grid.tiles[i]!;
    setShape(grid, i, shapeAt(from.grid, i));
  }
  const s = from.state;
  // Damage and fires go with their blocks.
  const take = (m: Record<string, number> | undefined) => {
    const out: Record<string, number> = {};
    for (const i of cells) {
      if (m?.[i] === undefined) continue;
      out[i] = m[i]!;
      delete m[i];
    }
    return Object.keys(out).length ? out : undefined;
  };
  return {
    state: {
      id,
      position: { ...s.position },
      velocity: vAdd(s.velocity, kick),
      angle: s.angle,
      angularVelocity: s.angularVelocity,
      fuel: 0,
      turrets: {},
      water: 0,
      throttle: { x: 0, y: 0 },
      pilotId: null,
      damage: take(s.damage),
      fires: take(s.fires),
    },
    grid,
    stats: shipStats(grid),
  };
}

/** A one-block ship centred on world point `center`, turned by `angle`; the block shaped by `shape` (see shapes.ts). */
export function looseBlock(id: string, tile: TileId, center: Vec2, angle: number, velocity: Vec2, shape = 0): ShipBody {
  const grid = makeGrid(SHIP_GRID.w, SHIP_GRID.h);
  const cx = Math.floor(grid.w / 2);
  const cy = Math.floor(grid.h / 2);
  grid.tiles[cy * grid.w + cx] = tile;
  setShape(grid, cy * grid.w + cx, shape);
  const half = v2(grid.w / 2, grid.h / 2);
  const position = vSub(vSub(center, half), vRotate(v2(cx + 0.5 - half.x, cy + 0.5 - half.y), angle));
  return {
    state: { id, position, velocity: { ...velocity }, angle, angularVelocity: 0, fuel: 0, water: 0, throttle: { x: 0, y: 0 }, turrets: {}, pilotId: null },
    grid,
    stats: shipStats(grid),
  };
}

/** Index of the ship tile hit by a world point, or -1. */
export function shipCellAt(ship: ShipBody, point: Vec2): number {
  const local = worldToShip(ship, point);
  const x = Math.floor(local.x);
  const y = Math.floor(local.y);
  return tileAt(ship.grid, x, y) !== T.EMPTY ? y * ship.grid.w + x : -1;
}

/** True if a world point is inside an asteroid tile that stops ships and cannonballs (anything but water). */
export function asteroidSolidAt(sim: Sim, point: Vec2): boolean {
  for (const a of sim.asteroids) {
    if (blocksShips(tileAt(a.grid, Math.floor(point.x - a.def.ox), Math.floor(point.y - a.def.oy)))) return true;
  }
  return false;
}

/** How much fuel, and how much water, a ship's furnaces hold: FURNACE.capacity per furnace. */
export function furnaceCapacity(ship: ShipBody): number {
  return ship.stats.furnaces * FURNACE.capacity;
}

/**
 * Feed a ship's furnaces from an inventory: just `item` when given (if it is
 * fuel or water and there's room for it), otherwise one fuel (coal before
 * starbark) and one water. Takes what it uses out of `inv`; returns it.
 */
export function feedFurnace(ship: ShipBody, inv: Inventory, item?: ItemId): Inventory {
  const used: Inventory = {};
  const cap = furnaceCapacity(ship);
  const fuelItem = item ? (ITEMS[item].fuel ? item : undefined) : fuelToStoke(inv);
  const waterItem: ItemId | undefined = item ? (ITEMS[item].water ? item : undefined) : waterToStoke(inv);
  if (fuelItem && count(inv, fuelItem) > 0 && ship.state.fuel < cap) {
    ship.state.fuel = Math.min(cap, ship.state.fuel + ITEMS[fuelItem].fuel!);
    used[fuelItem] = 1;
  }
  if (waterItem && count(inv, waterItem) > 0 && ship.state.water < cap) {
    ship.state.water = Math.min(cap, ship.state.water + ITEMS[waterItem].water!);
    used[waterItem] = 1;
  }
  addItems(inv, used, -1);
  return used;
}
